import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { crmApi, errCode, errMsg } from '@crm/api';
import { ClientStatusSelect } from '@crm/components/ClientStatusSelect';
import { TrialDateModal } from '@crm/components/TrialDateModal';
import {
  describeTrialDate,
  localIsoToday,
  TRIAL_DATE_ERROR_LABELS,
} from '@crm/utils/trialDate';
import { UiAutosuggest, UiButton } from '@shared/common/ui';

/**
 * Боковая панель быстрого управления направлениями и статусами клиента.
 */
export function ClientsQuickEditPanel({
  open,
  clientId,
  clientFio,
  statuses = [],
  canManageDirections = false,
  canUpdateStatus = false,
  listSearch = '',
  onClose,
  onChanged,
}) {
  const { ownerId, branchId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [directionQuery, setDirectionQuery] = useState('');
  const [directionSuggestions, setDirectionSuggestions] = useState([]);
  const [directionDraftId, setDirectionDraftId] = useState('');
  const [directionDraftLabel, setDirectionDraftLabel] = useState('');

  // Запрос даты пробного: { direction, status, mode }.
  const [trialRequest, setTrialRequest] = useState(null);
  const [trialError, setTrialError] = useState(null);
  const today = useMemo(() => localIsoToday(), []);

  const loadDetail = useCallback(async () => {
    if (!branchId || !clientId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await crmApi(branchId).get(`/clients/${clientId}`);
      setDetail(data.client ?? null);
    } catch (e) {
      setDetail(null);
      setError(errMsg(e, 'Не удалось загрузить клиента'));
    } finally {
      setLoading(false);
    }
  }, [branchId, clientId]);

  useEffect(() => {
    if (!open || !clientId) {
      setDetail(null);
      setError(null);
      setDirectionQuery('');
      setDirectionSuggestions([]);
      setDirectionDraftId('');
      setDirectionDraftLabel('');
      return undefined;
    }
    loadDetail();
    return undefined;
  }, [open, clientId, loadDetail]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Пока открыта модалка даты, Escape закрывает её, а не всю панель.
      if (trialRequest) {
        setTrialRequest(null);
        setTrialError(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, trialRequest]);

  useEffect(() => {
    if (!open || !canManageDirections || !branchId || directionDraftId || directionQuery.trim().length < 2) {
      setDirectionSuggestions([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/directions', {
          params: { q: directionQuery.trim(), limit: 30 },
        });
        if (!alive) return;
        const used = new Set(
          (detail?.directions ?? [])
            .filter((d) => d.isActive !== false)
            .map((d) => d.directionId ?? d.id),
        );
        setDirectionSuggestions((data.items ?? []).filter((d) => !used.has(d.id)));
      } catch {
        if (alive) setDirectionSuggestions([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, branchId, canManageDirections, detail?.directions, directionDraftId, directionQuery]);

  if (!open || !clientId) return null;

  const clearDirectionDraft = () => {
    setDirectionDraftId('');
    setDirectionDraftLabel('');
    setDirectionQuery('');
    setDirectionSuggestions([]);
  };

  const notifyChanged = async () => {
    await loadDetail();
    onChanged?.();
  };

  const onAddDirection = async () => {
    if (!directionDraftId) return;
    setBusy(true);
    setError(null);
    try {
      await crmApi(branchId).post(`/clients/${clientId}/directions`, {
        directionId: directionDraftId,
      });
      clearDirectionDraft();
      await notifyChanged();
    } catch (e) {
      setError(errMsg(e, 'Не удалось добавить направление'));
    } finally {
      setBusy(false);
    }
  };

  const saveStatus = async (direction, nextStatus, trialLessonDate) => {
    const body = { status: nextStatus };
    if (trialLessonDate !== undefined) body.trialLessonDate = trialLessonDate;
    await crmApi(branchId).patch(
      `/clients/${clientId}/directions/${direction.clientDirectionId}/status`,
      body,
    );
    await notifyChanged();
  };

  const onStatusChange = async (direction, nextStatus) => {
    if (!direction.clientDirectionId || !nextStatus) return;
    setError(null);

    // Статус требует дату — сначала спрашиваем её, статус меняем вместе с датой (ТЗ §4–§5).
    const mode = statuses.find((s) => s.key === nextStatus)?.trialDateMode ?? 'none';
    if (mode !== 'none') {
      setTrialRequest({ direction, status: nextStatus, mode });
      return;
    }

    setBusy(true);
    try {
      await saveStatus(direction, nextStatus);
    } catch (e) {
      setError(errMsg(e, 'Не удалось изменить статус'));
    } finally {
      setBusy(false);
    }
  };

  /** Перенос даты без смены статуса (ТЗ §6). */
  const onTrialDateEdit = (direction) => {
    setError(null);
    setTrialRequest({
      direction,
      status: direction.status,
      mode: direction.trialDateMode === 'none' ? 'set_required' : direction.trialDateMode,
    });
  };

  const onTrialDateConfirm = async (date) => {
    if (!trialRequest) return;
    setBusy(true);
    setTrialError(null);
    try {
      await saveStatus(trialRequest.direction, trialRequest.status, date);
      setTrialRequest(null);
    } catch (e) {
      setTrialError(
        TRIAL_DATE_ERROR_LABELS[errCode(e)] ?? errMsg(e, 'Не удалось сохранить дату пробного'),
      );
    } finally {
      setBusy(false);
    }
  };

  const onCloseDirection = async (direction) => {
    const label = direction.name ?? direction.directionId ?? direction.id;
    if (!window.confirm(`Прекратить занятия по направлению «${label}»?`)) return;
    setBusy(true);
    setError(null);
    try {
      if (direction.clientDirectionId) {
        await crmApi(branchId).post(
          `/clients/${clientId}/directions/${direction.clientDirectionId}/close`,
        );
      } else {
        await crmApi(branchId).delete(
          `/clients/${clientId}/directions/${direction.directionId ?? direction.id}`,
        );
      }
      await notifyChanged();
    } catch (e) {
      setError(errMsg(e, 'Не удалось прекратить занятия'));
    } finally {
      setBusy(false);
    }
  };

  const title = detail?.fio || clientFio || 'Клиент';
  const roles = detail?.roles ?? [];
  const directions = detail?.directions ?? [];
  const summaryLabel =
    detail?.statusName ||
    statuses.find((s) => String(s.key) === String(detail?.status))?.name ||
    detail?.status ||
    '—';

  return (
    <>
      <div className="drawer-backdrop" role="presentation" onClick={onClose}>
        <aside
          className="drawer clients-quick-edit"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clients-quick-edit-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="drawer__header">
            <div>
              <h2 id="clients-quick-edit-title">{title}</h2>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                Быстрое редактирование · направления и статусы
              </div>
            </div>
            <UiButton variant="ghost" size="sm" type="button" onClick={onClose} aria-label="Закрыть">
              ×
            </UiButton>
          </header>

          <div className="clients-quick-edit__meta">
            <div>
              <span className="muted">Общий статус:</span>{' '}
              <strong>{loading && !detail ? '…' : summaryLabel}</strong>
            </div>
            {roles.length ? (
              <div className="muted" style={{ fontSize: 13 }}>
                Роли:{' '}
                {roles
                  .map((r) => (r === 'parent' ? 'Родитель' : r === 'student' ? 'Ученик' : r))
                  .join(', ')}
              </div>
            ) : null}
            <Link
              to={`/${ownerId}/crm/${branchId}/clients/${clientId}${listSearch}`}
              className="clients-page__name-link"
            >
              Открыть полную карточку →
            </Link>
          </div>

          {error ? <div className="form-error">{error}</div> : null}

          <section className="drawer__detail-block">
            <h3>Направления</h3>
            {canManageDirections ? (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <UiAutosuggest
                    className="field clients-page__autosuggest"
                    label="Добавить направление"
                    value={directionDraftId ? directionDraftLabel : directionQuery}
                    onChange={(value) => {
                      if (directionDraftId) clearDirectionDraft();
                      setDirectionQuery(value);
                    }}
                    suggestions={directionSuggestions}
                    onSelect={(item) => {
                      setDirectionDraftId(item.id);
                      setDirectionDraftLabel(item.name ?? item.id);
                      setDirectionQuery('');
                      setDirectionSuggestions([]);
                    }}
                    placeholder="Начните вводить название..."
                    onClear={clearDirectionDraft}
                    emptyText=""
                    getOptionKey={(item) => item.id}
                    getOptionLabel={(item) => item.name}
                    disabled={busy || loading}
                  />
                </div>
                <UiButton
                  type="button"
                  size="sm"
                  disabled={!directionDraftId || busy || loading}
                  onClick={onAddDirection}
                  aria-label="Добавить направление"
                >
                  +
                </UiButton>
              </div>
            ) : null}

            {loading && !detail ? (
              <div className="muted">Загрузка…</div>
            ) : (
              <ul className="plain-list">
                {directions.map((d) => (
                  <li
                    key={d.clientDirectionId ?? d.id}
                    className="plain-list__item clients-quick-edit__dir-row"
                  >
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <div>
                        {d.name ?? d.directionId ?? d.id}
                        {d.isActive === false ? ' (закрыто)' : ''}
                      </div>
                      {d.startedAt ? (
                        <div className="muted" style={{ fontSize: 12 }}>
                          с {String(d.startedAt).slice(0, 10)}
                          {d.endedAt ? ` по ${String(d.endedAt).slice(0, 10)}` : ''}
                        </div>
                      ) : null}
                      {d.isActive !== false && canUpdateStatus && d.clientDirectionId ? (
                        <div className="clients-page__trial-row">
                          {(() => {
                            const info = describeTrialDate(d.trialLessonDate, today);
                            if (!info) return <span className="muted">Пробное: не назначено</span>;
                            return (
                              <span className={`clients-page__trial clients-page__trial--${info.tone}`}>
                                {info.text}
                              </span>
                            );
                          })()}
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => onTrialDateEdit(d)}
                          >
                            {d.trialLessonDate ? 'Перенести' : 'Назначить'}
                          </UiButton>
                        </div>
                      ) : null}
                    </div>
                    {d.isActive !== false && canUpdateStatus && d.clientDirectionId ? (
                      <div style={{ minWidth: 150, flex: '0 1 180px' }}>
                        <ClientStatusSelect
                          value={d.status ?? ''}
                          onChange={(key) => onStatusChange(d, key)}
                          statuses={statuses}
                          disabled={busy}
                          allowEmpty={false}
                          emptyLabel="—"
                          fallbackName={d.statusName}
                        />
                      </div>
                    ) : (
                      <span className="muted">{d.statusName || d.status || '—'}</span>
                    )}
                    {canManageDirections && d.isActive !== false ? (
                      <UiButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onCloseDirection(d)}
                        title="Прекратить занятия"
                      >
                        Прекратить
                      </UiButton>
                    ) : null}
                  </li>
                ))}
                {!directions.length ? (
                  <li className="clients-page__detail-empty">
                    {roles.includes('parent') && !roles.includes('student')
                      ? 'Собственных направлений нет'
                      : 'Нет направлений'}
                  </li>
                ) : null}
              </ul>
            )}
          </section>
        </aside>
      </div>

      {trialRequest && (
        <TrialDateModal
          title="Для этого статуса нужно указать дату пробного занятия"
          subtitle={trialRequest.direction.name ?? undefined}
          mode={trialRequest.mode}
          currentDate={trialRequest.direction.trialLessonDate}
          today={today}
          busy={busy}
          error={trialError}
          onSubmit={onTrialDateConfirm}
          onClose={() => {
            setTrialRequest(null);
            setTrialError(null);
          }}
        />
      )}
    </>
  );
}
